import React from 'react';
import { Box } from '@mui/material';

// Counts glide to a new value (~250ms, ease-out) instead of jumping, so a move or a new search reads as a change.
// A count that first arrives after mount (first visit, nothing cached) counts up from 0 (~400ms) as the list
// assembles; one already known at mount (a cached reload) just shows. Reduced-motion users get the plain number.
const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const useTweenedNumber = (value, duration = 250) => {
    const [shown, setShown] = React.useState(value);
    const fromRef = React.useRef(value);
    React.useEffect(() => {
        if (typeof value !== 'number' || reducedMotion()) {
            fromRef.current = value;
            setShown(value);
            return undefined;
        }
        const arriving = typeof fromRef.current !== 'number';
        const from = arriving ? 0 : fromRef.current;
        if (from === value) { fromRef.current = value; setShown(value); return undefined; }
        const length = arriving ? 400 : duration;
        let frame = 0;
        const start = performance.now();
        const step = (now) => {
            // A frame's timestamp can predate `start`; clamp so the count never dips below its origin.
            const t = Math.max(0, Math.min(1, (now - start) / length));
            const eased = 1 - (1 - t) ** 3;
            const next = Math.round(from + (value - from) * eased);
            fromRef.current = next;
            setShown(next);
            if (t < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [value, duration]);
    return shown;
};

// Stays mounted from placeholder to number, so it sees the count arrive and can count up.
export const TweenedNumber = ({ value, placeholder = null }) => {
    const shown = useTweenedNumber(value);
    return <>{typeof shown === 'number' ? shown : placeholder}</>;
};

// Stand-in for a count that has not arrived yet: a short shimmering bar, same height as the digits.
export const CountPlaceholder = ({ width = 18 }) => (
    <Box
        component="span"
        role="status"
        aria-label="Loading count"
        sx={{
            display: 'inline-block', width, height: 10, borderRadius: '4px', verticalAlign: 'middle',
            background: 'linear-gradient(90deg, #EAECF0 25%, #F9FAFB 50%, #EAECF0 75%)', backgroundSize: '200% 100%',
            '@keyframes dfCountShimmer': { '0%': { backgroundPosition: '200% 0' }, '100%': { backgroundPosition: '-200% 0' } },
            animation: 'dfCountShimmer 1.2s ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
    />
);

export default useTweenedNumber;
